import React, { useCallback, useEffect, useRef } from 'react';
import { Animated, Pressable, StyleSheet, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Svg, { Circle, Line, Path } from 'react-native-svg';
import { useTheme } from '../theme/useTheme';

/**
 * Web counterpart to PhysicalLightSwitch.tsx (native). react-native-
 * reanimated's own barrel export (node_modules/react-native-reanimated/lib/
 * module/index.js) throws `Cannot read properties of undefined (reading
 * 'useReducedMotion')` under this project's webpack setup — a real,
 * package-internal ESM re-export ordering issue (reanimated's ecosystem
 * assumes Metro's bundling, not webpack's), not something fixable from
 * app code, and it hard-crashed the whole Profile screen (this component's
 * only call site) since nothing here caught it.
 *
 * Same toggle behavior and visual language (gradient tile, sun/moon glyphs,
 * status rail), built on RN core's own Animated API instead — reanimated's
 * press-depth micro-interaction and light-pulse burst are the only things
 * trimmed, since neither affects the actual dark/light toggle this control
 * exists for.
 */
const CONTROL_DURATION_MS = 300;

interface Props {
  scale?: number;
}

const SunSymbol = () => (
  <Svg width={28} height={28} viewBox="0 0 28 28" accessibilityElementsHidden>
    <Circle cx={14} cy={14} r={4.6} fill="none" stroke="#2D2925" strokeWidth={1.8} />
    <Line x1={14} y1={1.8} x2={14} y2={5.4} stroke="#2D2925" strokeWidth={1.7} strokeLinecap="round" />
    <Line x1={14} y1={22.6} x2={14} y2={26.2} stroke="#2D2925" strokeWidth={1.7} strokeLinecap="round" />
    <Line x1={1.8} y1={14} x2={5.4} y2={14} stroke="#2D2925" strokeWidth={1.7} strokeLinecap="round" />
    <Line x1={22.6} y1={14} x2={26.2} y2={14} stroke="#2D2925" strokeWidth={1.7} strokeLinecap="round" />
    <Line x1={5.4} y1={5.4} x2={8} y2={8} stroke="#2D2925" strokeWidth={1.7} strokeLinecap="round" />
    <Line x1={20} y1={20} x2={22.6} y2={22.6} stroke="#2D2925" strokeWidth={1.7} strokeLinecap="round" />
    <Line x1={20} y1={8} x2={22.6} y2={5.4} stroke="#2D2925" strokeWidth={1.7} strokeLinecap="round" />
    <Line x1={5.4} y1={22.6} x2={8} y2={20} stroke="#2D2925" strokeWidth={1.7} strokeLinecap="round" />
  </Svg>
);

const MoonSymbol = () => (
  <Svg width={27} height={27} viewBox="0 0 28 28" accessibilityElementsHidden>
    <Path
      d="M20.8 19.5A9.4 9.4 0 0 1 8.5 7.2a9.7 9.7 0 1 0 12.3 12.3Z"
      fill="none"
      stroke="#C7CDD7"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

/** Compact Profile control that owns presentation only; theme state stays global. */
const PhysicalLightSwitch: React.FC<Props> = ({ scale = 1 }) => {
  const { themeName, setThemeName, canChangeTheme } = useTheme();
  const isDaylight = themeName === 'daylight';
  const modeProgress = useRef(new Animated.Value(isDaylight ? 1 : 0)).current;

  useEffect(() => {
    Animated.timing(modeProgress, {
      toValue: isDaylight ? 1 : 0,
      duration: CONTROL_DURATION_MS,
      useNativeDriver: false,
    }).start();
  }, [isDaylight, modeProgress]);

  const handlePress = useCallback(() => {
    if (!canChangeTheme) return;
    setThemeName(isDaylight ? 'midnight' : 'daylight').catch(() => undefined);
  }, [canChangeTheme, isDaylight, setThemeName]);

  const railBackgroundColor = modeProgress.interpolate({
    inputRange: [0, 1],
    outputRange: ['#2A5872', '#E7A366'],
  });

  return (
    <Pressable
      accessibilityRole="switch"
      accessibilityState={{ checked: isDaylight }}
      accessibilityLabel={isDaylight ? 'Switch Tonyte to Midnight' : 'Switch Tonyte to Daylight'}
      hitSlop={8}
      onPress={handlePress}
      style={[styles.touchTarget, scale !== 1 && { transform: [{ scale }] }]}
    >
      <View style={styles.tile}>
        <Animated.View pointerEvents="none" style={[StyleSheet.absoluteFill, { opacity: Animated.subtract(1, modeProgress) }]}>
          <LinearGradient
            colors={['#2B374B', '#1C2737', '#101722']}
            locations={[0, 0.48, 1]}
            start={{ x: 0.12, y: 0 }}
            end={{ x: 0.88, y: 1 }}
            style={StyleSheet.absoluteFill}
          />
        </Animated.View>
        <Animated.View pointerEvents="none" style={[StyleSheet.absoluteFill, { opacity: modeProgress }]}>
          <LinearGradient
            colors={['#FFF5E7', '#F4E2CB', '#E7C89E']}
            locations={[0, 0.58, 1]}
            start={{ x: 0.12, y: 0 }}
            end={{ x: 0.9, y: 1 }}
            style={StyleSheet.absoluteFill}
          />
        </Animated.View>

        <View pointerEvents="none" style={styles.glassHighlight} />
        <View pointerEvents="none" style={styles.lowerShade} />

        <View pointerEvents="none" style={styles.contentStage}>
          <Animated.View style={[styles.modeContent, { opacity: Animated.subtract(1, modeProgress) }]}>
            <MoonSymbol />
            <Animated.Text style={[styles.modeLabel, styles.midnightLabel]}>MIDNIGHT</Animated.Text>
          </Animated.View>
          <Animated.View style={[styles.modeContent, { opacity: modeProgress }]}>
            <SunSymbol />
            <Animated.Text style={[styles.modeLabel, styles.daylightLabel]}>DAYLIGHT</Animated.Text>
          </Animated.View>
        </View>
      </View>

      <Animated.View pointerEvents="none" style={[styles.statusRail, { backgroundColor: railBackgroundColor }]}>
        <View style={styles.railSheen} />
      </Animated.View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  touchTarget: {
    width: 106,
    height: 82,
    position: 'relative',
    overflow: 'visible',
  },
  tile: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 8,
    bottom: 0,
    borderRadius: 25,
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    shadowOffset: { width: 0, height: 9 },
    shadowRadius: 15,
    elevation: 8,
  },
  glassHighlight: {
    position: 'absolute',
    left: 13,
    right: 13,
    top: 1,
    height: 1,
    borderRadius: 1,
    backgroundColor: 'rgba(255,255,255,0.33)',
  },
  lowerShade: {
    position: 'absolute',
    left: 10,
    right: 10,
    bottom: 0,
    height: 11,
    borderRadius: 14,
    backgroundColor: 'rgba(64,43,24,0.035)',
  },
  contentStage: {
    width: '100%',
    height: 58,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  modeContent: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 2,
    bottom: 0,
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: 1,
    paddingBottom: 2,
  },
  modeLabel: {
    fontSize: 8.7,
    lineHeight: 12,
    fontWeight: '900',
    letterSpacing: 1.35,
    textAlign: 'center',
  },
  midnightLabel: {
    color: 'rgba(218,224,234,0.86)',
  },
  daylightLabel: {
    color: '#302820',
  },
  statusRail: {
    position: 'absolute',
    left: 26,
    top: 3,
    width: 40,
    height: 8,
    borderRadius: 4,
    borderWidth: StyleSheet.hairlineWidth,
    overflow: 'visible',
  },
  railSheen: {
    position: 'absolute',
    left: 6,
    right: 5,
    top: 1,
    height: 1,
    borderRadius: 1,
    backgroundColor: 'rgba(255,255,255,0.42)',
  },
});

export default PhysicalLightSwitch;
